import { useNavigation } from '@react-navigation/native';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Platform, TouchableOpacity } from 'react-native';
import Animated, { cubicBezier } from 'react-native-reanimated';

import Avatar from '@components/Avatar/Avatar';
import { ProjectAvatar } from '@components/Avatar/ProjectAvatar';
import {
  MyTaskGroup,
  MyTasksGroupBy,
  isTaskOverdue,
  UNASSIGNED_GROUP_KEY,
} from '@components/MyTasks/myTasksGrouping';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Task, TaskPriority } from '@graphql/generated';
import { canEditTaskThirdLevel } from '@hooks/useTaskMembership';
import { AppStackScreenProps } from '@navigation/app-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import { localDateFromUTCDate } from '@utils/formatters/date';

const PROGRESS_EASING = cubicBezier(0.4, 0, 0.2, 1);

/**
 * The group's progress fill. It grows from 0 on mount (fresh per tab switch,
 * since each tab's groups carry different keys) and animates whenever
 * progress changes. Zero snaps instantly instead — otherwise a group whose
 * progress settles at 0 (e.g. a transient recount as data loads) visibly
 * animates the bar retreating back down to empty.
 *
 * The width is a plain style React owns, animated by a CSS transition. It
 * used to live in a shared value applied on the UI thread, which React only
 * learns about once an animation settles: a re-render in between (e.g. the
 * next toggle in a burst) committed the last value React knew, so the bar
 * snapped back to an old width for a moment while the count next to it was
 * already right (TA-11018, seen on device in search). Its own component, so
 * the mount re-render that starts the grow-in stays here instead of
 * re-rendering the whole card.
 */
const ProgressFill: React.FC<{ progress: number; testID: string }> = ({
  progress,
  testID,
}) => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return (
    <Animated.View
      testID={testID}
      style={{
        height: 8,
        borderRadius: theme.borderRadii.xxxs,
        backgroundColor: theme.colors.greenSecondary,
        width: `${hasMounted ? progress * 100 : 0}%`,
        transitionProperty: 'width',
        transitionDuration: progress === 0 ? 0 : 450,
        transitionTimingFunction: PROGRESS_EASING,
      }}
    />
  );
};

type MyTaskGroupCardProps = {
  group: MyTaskGroup;
  groupBy: MyTasksGroupBy;
  onToggleComplete: (task: Task) => void;
  /**
   * Card style (AC16): 1px border only when the card sits on a white
   * background; skipped on light-grey backgrounds.
   */
  onWhiteBackground?: boolean;
  /** Unscheduled variant (AC4): count badge, no progress bar. */
  unscheduled?: boolean;
  /**
   * 7 Days tab: render just the task count (e.g. "3 tasks") instead of the
   * completed/total progress bar.
   */
  hideProgressBar?: boolean;
  /**
   * Project-scoped screens (ProjectTasksScreen) never show the per-row
   * project name/avatar — every task already belongs to the same project.
   */
  hideProjectName?: boolean;
  /** Unscheduled only: a next page exists — count badge shows "N+" and a
   * "Load More" button renders below the last row. */
  hasMore?: boolean;
  /** Unscheduled only: shows a loading indicator below the last row instead
   * of the "Load More" button, while a press-triggered fetch is in flight. */
  loadingMore?: boolean;
  /** Unscheduled only: called when the "Load More" button is pressed. */
  onLoadMore?: () => void;
  /**
   * Server-grouped cards only (TA-10446): loads the next page of this
   * group's own open tasks once `group.tasksHasMore` is true. Unlike
   * Unscheduled's hasMore/loadingMore/onLoadMore (a flat, independently
   * paginated listTasks query, button-driven), a normal group card fires
   * this as soon as it renders (and again after each successful page,
   * chaining through every remaining page) — a FlatList row already only
   * mounts once it's within the render window, so mount is a good enough
   * proxy for "about to be seen".
   */
  onLoadMoreTasks?: () => Promise<unknown> | void;
};

const priorityCircleColor = (
  priority: Task['priority']
): keyof typeof theme.colors => {
  switch (priority) {
    case TaskPriority.High:
      return 'alertRed';
    case TaskPriority.Medium:
      return 'pastelYellow';
    default:
      return 'blue';
  }
};

const PriorityCircle = React.memo(function PriorityCircle({
  task,
  onToggle,
  disabled,
}: {
  task: Task;
  onToggle: () => void;
  // True when the current user lacks edit permission on this task.
  disabled: boolean;
}) {
  const isCompleted = !!task.completedAt;
  let accessibilityLabel = `Mark complete | ${task.name}`;
  if (isCompleted) {
    accessibilityLabel = `Mark incomplete | ${task.name}`;
  }
  // Matches RadioButton's (TaskDetail) disabled treatment: no permission
  // swaps the priority color for the same grey03 ring / grey02 fill that
  // RadioButton's StateNo-over-StateDisabled icon layering renders, instead
  // of dimming the priority color. A completed-but-disabled icon stays
  // greenSecondary on native (dimmed via opacity) but turns grey04 on web.
  let icon = (
    <Box
      width={20}
      height={20}
      borderRadius='s'
      borderWidth={2}
      backgroundColor={disabled ? 'grey02' : undefined}
      style={{
        borderColor:
          theme.colors[
            disabled ? 'grey03' : priorityCircleColor(task.priority)
          ],
      }}
    />
  );
  if (isCompleted) {
    const completedColor =
      disabled && Platform.OS === 'web' ? 'grey04' : 'greenSecondary';
    icon = <Icon name='StateYes' variant='m' color={completedColor} />;
  }
  const isCompletedAndDisabledOnNative =
    disabled && isCompleted && Platform.OS !== 'web';
  return (
    <TouchableOpacity
      accessibilityLabel={accessibilityLabel}
      // Not the native `disabled` prop — that swallows the touch entirely,
      // which bubbles it up to the row's outer TouchableOpacity instead of
      // stopping it here, accidentally opening the task detail screen.
      // Letting onToggle's own guards (permission here, repeat tap in
      // TasksListView) no-op the tap keeps the touch captured on this icon.
      style={isCompletedAndDisabledOnNative ? { opacity: 0.5 } : undefined}
      hitSlop={{
        top: theme.spacing.xs,
        bottom: theme.spacing.xs,
        left: theme.spacing.xs,
        right: theme.spacing.xs,
      }}
      onPress={onToggle}>
      {icon}
    </TouchableOpacity>
  );
});

const DueDateChip = React.memo(function DueDateChip({
  task,
  canComplete,
}: {
  task: Task;
  canComplete: boolean;
}) {
  const { t: f } = useTranslation('format');
  const appNavigation =
    useNavigation<AppStackScreenProps<'tasks-stack'>['navigation']>();

  const isOverdue = isTaskOverdue(task);
  const handlePress = useCallback(() => {
    // task-edit-date-modal lives on the app stack (reachable from anywhere,
    // including this screen's tasks-tab-stack) and already handles setting a
    // due date from empty — unlike task-detail-edit-dates, which only lives
    // on the nested tasks-stack/task-panel navigators and can't be reached
    // from here, so it's not usable for the unscheduled group's tasks.
    appNavigation.navigate('task-edit-date-modal', {
      taskId: task.id,
      status: task.status,
      startDate: task.startDate,
      dueDate: task.dueDate,
    });
  }, [appNavigation, task]);

  let textColor: 'grey04' | 'alertRed' | 'textSecondary' = 'textSecondary';
  let borderColor: 'alertRed' | 'grey02' = isOverdue ? 'alertRed' : 'grey02';
  let backgroundColor: 'grey02' | undefined;
  if (!canComplete) {
    // Match TaskDetail's disabled due-date button (Button.tsx disabled style).
    backgroundColor = 'grey02';
    borderColor = 'grey02';
    textColor = 'textSecondary';
  } else if (isOverdue) {
    textColor = 'alertRed';
  }

  return (
    <TouchableOpacity
      hitSlop={{ top: theme.spacing.xs, bottom: theme.spacing.xs }}
      testID='my-tasks-due-date-chip'
      disabled={!canComplete}
      onPress={handlePress}>
      <Box
        minWidth={50}
        marginLeft='s'
        px='xs'
        height={24}
        flexDirection='row'
        justifyContent='center'
        alignItems='center'
        borderWidth={1}
        borderColor={borderColor}
        backgroundColor={backgroundColor}
        borderRadius='s'>
        {!task.dueDate && (
          <Icon
            variant='xxs'
            marginRight='xxs'
            name='Calendar'
            color='textSecondary'
          />
        )}
        <Text variant='metadata' color={textColor}>
          {task.dueDate
            ? f('monthAndDayYearV3', {
                val: localDateFromUTCDate(task.dueDate),
              })
            : 'Due Date'}
        </Text>
      </Box>
    </TouchableOpacity>
  );
});

const GroupTaskRow = React.memo(function GroupTaskRow({
  task,
  showProject,
  onToggleComplete,
  isLast = false,
}: {
  task: Task;
  showProject: boolean;
  onToggleComplete: (task: Task) => void;
  /** Last row rendered in the card (TA-10495 #2): the bottom border sits on
   * this row's divider container, not the card, so it must be dropped here
   * to avoid a trailing border under the card's final item. */
  isLast?: boolean;
}) {
  const { navigateToTask } = useAppNavigation();
  // Truncation (AC15): task name 2 lines when alone, 1 line when a project
  // name renders below it (project name itself capped at 1 line).
  const showProjectRow = showProject && !!task.project;
  const canComplete = useMemo(
    () => canEditTaskThirdLevel(task),
    [
      task.currentUserRole,
      task.currentUserProjectRole,
      task.currentUserProjectTeamRole,
    ]
  );
  const handlePress = useCallback(
    () => navigateToTask(task),
    [navigateToTask, task]
  );
  const handleToggle = useCallback(() => {
    if (!canComplete) return;
    onToggleComplete(task);
  }, [canComplete, onToggleComplete, task]);

  return (
    <TouchableOpacity testID={`my-tasks-row-${task.id}`} onPress={handlePress}>
      <Box flexDirection='row' alignItems='center'>
        <Box marginRight='s'>
          <PriorityCircle
            task={task}
            onToggle={handleToggle}
            disabled={!canComplete}
          />
        </Box>
        {/* Divider starts here, after the checkbox — matches Figma (Design
            Blueprint, node 184-84583): the border lives on this container,
            not on the row as a whole. */}
        <Box
          // Not `my-tasks-row-…`: several tests count rows with
          // /^my-tasks-row-/, and a divider sharing that prefix doubles the
          // match count.
          testID={`my-tasks-divider-${task.id}`}
          flex={1}
          flexDirection='row'
          alignItems='center'
          paddingVertical='s'
          borderBottomColor='grey02'
          borderBottomWidth={isLast ? 0 : 1}>
          <Box flex={1}>
            <Text
              variant='webBodySecondary'
              color='textPrimary'
              numberOfLines={2}>
              {task.name}
            </Text>
            {showProjectRow && (
              <Box flexDirection='row' marginTop='xxs' alignItems='center'>
                <ProjectAvatar
                  icon={task.project?.icon}
                  color={task.project?.color}
                />
                <Text
                  marginLeft='xxs'
                  variant='metadataSmall'
                  color='grey04'
                  numberOfLines={1}
                  flexShrink={1}>
                  {task.project?.name}
                </Text>
              </Box>
            )}
          </Box>
          <DueDateChip task={task} canComplete={canComplete} />
        </Box>
      </Box>
    </TouchableOpacity>
  );
});

/**
 * Group card for the My Tasks revamp (TA-10186): headline capped at 2 lines,
 * progress bar, default-sorted task rows, and completed items collapsed by
 * default (AC10). Card style per AC16: no shadow, 8px radius, 16px padding,
 * border only on white backgrounds.
 */
export const MyTaskGroupCard = React.memo(function MyTaskGroupCard({
  group,
  groupBy,
  onToggleComplete,
  onWhiteBackground = true,
  unscheduled = false,
  hideProgressBar = false,
  hideProjectName = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onLoadMoreTasks,
}: MyTaskGroupCardProps) {
  const { t } = useTranslation();
  const [showCompleted, setShowCompleted] = useState(false);

  // Server-grouped "load more of this group's own tasks" (TA-10446) — see
  // onLoadMoreTasks prop doc above. Guarded by a ref (not just the loading
  // state) so a fetch already in flight is never re-triggered by an
  // unrelated re-render; re-arms once that fetch settles, so multiple
  // remaining pages chain through automatically.
  const [tasksLoadingMore, setTasksLoadingMore] = useState(false);
  const tasksLoadMoreInFlightRef = useRef(false);
  useEffect(() => {
    if (!onLoadMoreTasks || !group.tasksHasMore) {
      tasksLoadMoreInFlightRef.current = false;
      return;
    }
    if (tasksLoadMoreInFlightRef.current) return;
    tasksLoadMoreInFlightRef.current = true;
    setTasksLoadingMore(true);
    Promise.resolve(onLoadMoreTasks()).finally(() => {
      tasksLoadMoreInFlightRef.current = false;
      setTasksLoadingMore(false);
    });
  }, [group.tasksHasMore, group.tasks.length, onLoadMoreTasks]);

  // Server-grouped path (MyTasksScreen) carries the true per-group totals
  // straight from ProjectTaskGroup/PriorityTaskGroup/AssigneeTaskGroup —
  // each group's own tasks/completedTasks connection is capped at first:100,
  // so counting the loaded arrays would undercount past that. The
  // client-grouped path (ProjectTasksScreen's groupTasks) has no server
  // total and falls back to the loaded arrays' length.
  const openCount = group.taskCount ?? group.tasks.length;
  const completedCount = group.completedCount ?? group.completedTasks.length;
  const totalCount = openCount + completedCount;
  const progress = totalCount === 0 ? 0 : completedCount / totalCount;
  // TA-10189 AC2: once every task in the group is done, the card renders
  // every completed row plainly — no Show/Hide completed toggle to collapse
  // them behind (My Day/7 Days never reach this state since that fully-
  // complete card disappears outright before it gets here).
  const isFullyComplete = openCount === 0 && completedCount > 0;
  // The last open task keeps its divider whenever a completed section follows
  // it — including the collapsed "Show completed" control. With no completed
  // section below, the final task remains borderless.
  const hasCompletedSection = completedCount > 0;

  // Unassigned (All Tasks, TA-10495 #10): gets the same count-badge
  // treatment as Unscheduled instead of the progress bar/count-under-title
  // every other group shows.
  const isUnassigned = group.key === UNASSIGNED_GROUP_KEY;

  const showProject =
    !hideProjectName && (groupBy !== MyTasksGroupBy.Project || unscheduled);
  // Unscheduled (flat bucket) and Unassigned (assignee:none, no assigneeUser)
  // aren't tied to a real assignee — never show the header avatar for either,
  // even when the current Group By is Assignee (or, on ProjectTasksScreen,
  // falls back to it).
  const showAssigneeAvatar =
    groupBy === MyTasksGroupBy.Assignee && !unscheduled && !!group.assigneeUser;

  return (
    <Box
      testID={`my-tasks-group-${group.key}`}
      backgroundColor='white'
      borderRadius='xs'
      padding='m'
      marginHorizontal='m'
      marginBottom='s'
      borderWidth={onWhiteBackground ? 1 : 0}
      borderColor='grey02'>
      <Box flexDirection='row' alignItems='center'>
        {showAssigneeAvatar && (
          <Box marginRight='xs'>
            <Avatar
              id={group.assigneeUser?.id ?? ''}
              avatar={group.assigneeUser?.avatar}
              label={group.title}
              labelVariant='webBodySecondary'
              width={32}
              height={32}
              accessibilityLabel={`${group.title} avatar`}
            />
          </Box>
        )}
        <Box flexShrink={1}>
          <Box flexDirection='row' alignItems='center'>
            <Text
              variant='labelEmphasizedChatDetialTitle'
              color='textPrimary'
              numberOfLines={2}
              flexShrink={1}>
              {group.title}
            </Text>
            {(unscheduled || isUnassigned) && (
              <Box
                marginLeft='xs'
                minWidth={20}
                height={20}
                px='xxs'
                borderRadius='xs'
                backgroundColor='grey01'
                alignItems='center'
                justifyContent='center'>
                <Text variant='metadataSmall' color='textSecondary'>
                  {unscheduled ? group.tasks.length : totalCount}
                  {unscheduled && hasMore ? '+' : ''}
                </Text>
              </Box>
            )}
          </Box>

          {!unscheduled && !isUnassigned && hideProgressBar && (
            <Text marginTop='xxs' variant='webBodyHintSubline' color='grey04'>
              {totalCount} {totalCount === 1 ? 'task' : 'tasks'}
            </Text>
          )}
        </Box>
      </Box>

      {!unscheduled && !isUnassigned && !hideProgressBar && (
        <Box marginTop='xs' flexDirection='row' alignItems='center'>
          <Box
            flex={1}
            height={8}
            borderRadius='xxs'
            backgroundColor='grey02'
            overflow='hidden'>
            <ProgressFill
              progress={progress}
              testID={`my-tasks-group-${group.key}-progress-fill`}
            />
          </Box>
          <Text marginLeft='xs' variant='webBodyHintSubline' color='grey04'>
            {completedCount}/{totalCount} tasks done
          </Text>
        </Box>
      )}

      <Box marginTop='xs'>
        {group.tasks.map((task, index) => (
          <GroupTaskRow
            key={task.id}
            task={task}
            showProject={showProject}
            onToggleComplete={onToggleComplete}
            isLast={!hasCompletedSection && index === group.tasks.length - 1}
          />
        ))}
        {unscheduled && hasMore && !loadingMore && (
          <TouchableOpacity
            hitSlop={{
              top: theme.spacing.xs,
              bottom: theme.spacing.xs,
              left: theme.spacing.xs,
              right: theme.spacing.xs,
            }}
            testID={`my-tasks-group-${group.key}-load-more`}
            accessibilityLabel={t('shared:loadMore')}
            onPress={onLoadMore}>
            <Box
              mt='xs'
              alignSelf='center'
              backgroundColor='grey01'
              borderRadius='xs'
              paddingHorizontal='m'
              paddingVertical='xs'>
              <Text variant='labelSmall' color='textPrimary'>
                {t('shared:loadMore')}
              </Text>
            </Box>
          </TouchableOpacity>
        )}
        {(loadingMore || tasksLoadingMore) && (
          <Box mt='xs' height={40} justifyContent='center' alignItems='center'>
            <ActivityIndicator
              testID={`my-tasks-group-${group.key}-loading-more`}
              size={25}
              color={theme.colors.textPrimary}
            />
          </Box>
        )}
      </Box>

      {completedCount > 0 && (
        <>
          {(showCompleted || isFullyComplete) &&
            group.completedTasks.map((task, index) => (
              <GroupTaskRow
                key={task.id}
                task={task}
                showProject={showProject}
                onToggleComplete={onToggleComplete}
                isLast={
                  isFullyComplete && index === group.completedTasks.length - 1
                }
              />
            ))}
          {!isFullyComplete && (
            <TouchableOpacity
              testID={`my-tasks-group-${group.key}-toggle-completed`}
              onPress={() => setShowCompleted((prev) => !prev)}>
              <Box
                flexDirection='row'
                height={32}
                alignItems='flex-end'
                justifyContent='center'>
                <Text
                  variant='labelAssignedTask'
                  color='textSecondary'
                  style={{
                    includeFontPadding: false,
                    textAlignVertical: 'center',
                  }}>
                  {showCompleted
                    ? 'Hide completed'
                    : `Show completed (${completedCount})`}
                </Text>
                <Icon
                  name={showCompleted ? 'ChevronUp' : 'ChevronDown'}
                  variant='s'
                  color='textSecondary'
                  marginLeft='xs'
                />
              </Box>
            </TouchableOpacity>
          )}
        </>
      )}
    </Box>
  );
});
